import type { ProdantixClient } from 'ProdantixSdk/core/client';
import { CTA_CLICKED, CTA_DISMISSED, CTA_VIEWED, type CtaDevice, type ServedCta } from 'ProdantixSdk/core/ctas';
import { isDue, lastShown, rememberShown, shownThisSession } from 'ProdantixSdk/web/ctas/frequency';
import { type RenderedCta, renderCta } from 'ProdantixSdk/web/ctas/render';
import { watchTrigger } from 'ProdantixSdk/web/ctas/triggers';
import { formsHost } from 'ProdantixSdk/web/forms/index';
import { type FormsHost, renderForm } from 'ProdantixSdk/web/forms/render';
import { FORM_STRINGS, type FormLanguage, formLanguage } from 'ProdantixSdk/web/forms/strings';

export { isDue } from 'ProdantixSdk/web/ctas/frequency';
export type { RenderCtaOptions, RenderedCta } from 'ProdantixSdk/web/ctas/render';
export { renderCta } from 'ProdantixSdk/web/ctas/render';
export { watchTrigger } from 'ProdantixSdk/web/ctas/triggers';

/** What the CTAs need from the SDK: the page's CTAs, their events, flags as
 *  the client decides them, and forms for a button that opens one. */
export interface CtasHost {
  capture(event: string, cta: ServedCta, button?: number): void;
  ctas(page: { device: CtaDevice; language: string; url: string }): Promise<ServedCta[]>;
  forms: FormsHost;
  isEnabled(flag: string): Promise<boolean>;
  variant(flag: string): Promise<string | null>;
}

export const ctasHost = (client: ProdantixClient): CtasHost => ({
  capture: (event, cta, button) => client.captureCta(event, cta, button),
  ctas: page => client.getCtas(page),
  forms: formsHost(client),
  isEnabled: flag => client.isFeatureEnabled(flag),
  variant: async flag => (await client.getVariant(flag))?.key ?? null,
});

/** The device class a viewport width reads as. */
export const deviceOf = (width: number): CtaDevice => (width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop');

/** Whether the CTA's flag rule lets it show: no rule, the flag on, or the
 *  flag answering the named variant. */
export const flagAllows = async (host: CtasHost, cta: ServedCta): Promise<boolean> => {
  const flag = cta.flag;
  if (!flag) {
    return true;
  }
  if (flag.variant) {
    return (await host.variant(flag.key)) === flag.variant;
  }
  return host.isEnabled(flag.key);
};

const openForm = (formId: string, host: FormsHost, language: FormLanguage): void => {
  const dialog = document.createElement('dialog');
  dialog.className = 'prodantix-form-popup';
  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = '×';
  close.setAttribute('aria-label', FORM_STRINGS[language].close);
  close.title = FORM_STRINGS[language].close;
  close.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => dialog.remove());
  dialog.append(close);
  document.body.append(dialog);
  renderForm(dialog, { formId, host, language });
  dialog.showModal();
};

const openMessenger = (): void => {
  const messenger = (window as { prodantix?: unknown }).prodantix;
  if (typeof messenger === 'function') {
    messenger('open');
  }
};

export interface RunCtasOptions {
  language: FormLanguage;
  now?: () => number;
  /** Opens an address; the page's own navigation by default. */
  open?: (url: string) => void;
}

/** Reads the page's CTAs and shows each one its flag, frequency and trigger
 *  allow, sending its view, clicks and dismissal. Answers a function that
 *  takes every CTA of this page down and stops waiting on their triggers. */
export const runCtas = async (host: CtasHost, options: RunCtasOptions): Promise<() => void> => {
  const now = options.now ?? Date.now;
  const open = options.open ?? ((url: string) => window.location.assign(url));
  const stops: (() => void)[] = [];
  const shown: RenderedCta[] = [];
  let isStopped = false;
  const stopAll = (): void => {
    isStopped = true;
    for (const stop of stops.splice(0)) {
      stop();
    }
    for (const rendered of shown.splice(0)) {
      rendered.destroy();
    }
  };
  const ctas = await host.ctas({ device: deviceOf(window.innerWidth), language: document.documentElement.lang || options.language, url: window.location.href });
  for (const cta of ctas) {
    if (isStopped || !isDue(cta.frequency, lastShown(cta.id), shownThisSession(cta.id), now()) || !(await flagAllows(host, cta))) {
      continue;
    }
    stops.push(
      watchTrigger(cta.trigger, () => {
        if (isStopped) {
          return;
        }
        const rendered: RenderedCta = renderCta(cta, {
          language: options.language,
          onButton: index => {
            host.capture(CTA_CLICKED, cta, index);
            const action = cta.variant.content.buttons[index]?.action;
            if (!action) {
              return;
            }
            if (action.kind !== 'url' || cta.kind !== 'embedded') {
              rendered.destroy();
            }
            if (action.kind === 'url') {
              open(action.value);
            } else if (action.kind === 'form') {
              openForm(action.value, host.forms, options.language);
            } else if (action.kind === 'messenger') {
              openMessenger();
            } else {
              host.capture(CTA_DISMISSED, cta);
            }
          },
          onDismiss: () => host.capture(CTA_DISMISSED, cta),
        });
        if (rendered.elements.length === 0) {
          return;
        }
        shown.push(rendered);
        rememberShown(cta.id, now());
        host.capture(CTA_VIEWED, cta);
      }),
    );
  }
  return stopAll;
};

/** Runs the CTAs for the page and again on every in-page navigation, the way
 *  a single-page app moves between pages. Answers a function that stops. */
const NO_CTAS = (): void => undefined;

export const startCtas = (client: ProdantixClient, language?: string): (() => void) => {
  const host = ctasHost(client);
  const pageLanguage = formLanguage(language ?? document.documentElement.lang);
  const run = (): Promise<() => void> =>
    runCtas(host, { language: pageLanguage }).catch((error: unknown) => {
      console.error('[prodantix] the CTAs for this page could not be read', error);
      return NO_CTAS;
    });
  let current: Promise<() => void> = run();
  let lastUrl = window.location.href;
  const rerun = (): void => {
    if (window.location.href === lastUrl) {
      return;
    }
    lastUrl = window.location.href;
    const previous = current;
    current = previous.then(stop => {
      stop();
      return run();
    });
  };
  const originalPush = history.pushState.bind(history);
  const originalReplace = history.replaceState.bind(history);
  history.pushState = (...args: Parameters<History['pushState']>): void => {
    originalPush(...args);
    rerun();
  };
  history.replaceState = (...args: Parameters<History['replaceState']>): void => {
    originalReplace(...args);
    rerun();
  };
  window.addEventListener('popstate', rerun);
  return () => {
    history.pushState = originalPush;
    history.replaceState = originalReplace;
    window.removeEventListener('popstate', rerun);
    void current.then(stop => stop());
  };
};
